import React, { useState } from 'react';
import { Button, Modal, TextArea } from '@morressier/alembic';
import { Flex, Text } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/pro-light-svg-icons';
import { useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { IBlockedReviewer } from '@publishing-engine/domain/views';
import { useReviewerValidation } from '../../../../hooks/useReviewerValidation.js';
import { useExistingReviewersContext } from '../../../../contexts/ExistingReviewersProvider.js';
import ReviewersInfoWarningModal from '../ReviewersInfoWarningModal.js';
import { faWarning } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  onClose: () => void;
  onAddEmails: (emails: string[]) => void;
  buttonLabel?: 'Add' | 'Assign';
  blockedReviewers?: IBlockedReviewer[];
  requireUserExistence?: boolean;
}

type FormData = {
  emails: string;
};

const AddEmailsModal: React.FC<Props> = ({
  onClose,
  onAddEmails,
  buttonLabel = 'Add',
  blockedReviewers = [],
  requireUserExistence = false
}) => {
  const methods = useForm({
    defaultValues: {
      emails: ''
    }
  });
  const {
    getValues,
    setError,
    clearErrors,
    formState: { errors }
  } = methods;
  const emails = methods.watch('emails');

  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [warningNInfoModal, setWarningNInfoModal] = useState(false);
  const [infoMessages, setInfoMessages] = useState<Record<string, string>>({});
  const [warningMessages, setWarningMessages] = useState<
    Record<string, string>
  >({});

  const { existingReviewers } = useExistingReviewersContext();

  const types = errors['emails']?.types;

  const { validateBulkReviewers } = useReviewerValidation(
    setError,
    blockedReviewers,
    'emails',
    requireUserExistence
  );

  const getEmails = (reviewers: string) => {
    const lines = reviewers.split('\n');
    const trimmedLines = lines.map(line => line.trim().toLowerCase());
    const emails = trimmedLines.map(email => ({ email }));
    return emails;
  };

  const validateOnSubmit = async () => {
    clearErrors('emails');
    setIsValidating(true);

    const reviewers = getEmails(getValues('emails'));
    const validation = await validateBulkReviewers(
      reviewers,
      existingReviewers
    );

    const disallowedInfoMap: Record<string, string> = {};
    const allowedInfoMap: Record<string, string> = {};
    const warningMap: Record<string, string> = {};

    validation?.forEach(item => {
      if (item.info?.length) {
        const disallowed = item.info.filter(i => !i.isAllowed);
        const allowed = item.info.filter(i => i.isAllowed);

        if (disallowed.length) {
          disallowedInfoMap[item.email] = disallowed
            .map(i => i.message)
            .join(', ');
        }

        if (allowed.length) {
          allowedInfoMap[item.email] = allowed.map(i => i.message).join(', ');
        }
      }

      if (item.warning?.length) {
        warningMap[item.email] = item.warning.map(w => w.message).join(', ');
      }
    });

    setInfoMessages(allowedInfoMap);
    setWarningMessages(warningMap);

    if (Object.keys(disallowedInfoMap).length > 0) {
      setError('emails', {
        types: disallowedInfoMap
      });
    }

    setIsValidating(false);
    const field = methods.getFieldState('emails');
    if (
      !field.error &&
      !Object.keys(disallowedInfoMap).length &&
      (Object.keys(allowedInfoMap).length || Object.keys(warningMap).length)
    ) {
      setWarningNInfoModal(true);
      return false;
    }

    return !field.error && Object.keys(disallowedInfoMap).length === 0;
  };

  const onSubmit = async (data: FormData) => {
    const isValid = await validateOnSubmit();
    if (!isValid) return; // Stop if validation added errors

    const lines = data.emails.split('\n');
    const trimmedLines = lines.map(l => l.trim().toLowerCase());

    onAddEmails(trimmedLines);
    onClose();
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      width="720px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <form
        onSubmit={async e => {
          e.stopPropagation();
          e.preventDefault();
          await onSubmit(methods.getValues());
        }}
      >
        <Flex flexDirection="column" gap={6} padding={8}>
          <FontAwesomeIcon
            icon={faUserPlus}
            style={{ width: '40px', height: '40px' }}
          />

          <Flex flexDirection="column" gap={2}>
            <FieldLabel
              label="Enter Reviewer Emails"
              subText="Place each email on a separate line to ensure all emails are correctly processed by the system."
              isRequired={false}
            />
            <TextArea
              value={emails}
              placeholder="Enter emails, one per line with no commas at the end"
              {...methods.register('emails')}
              isInvalid={!!errors.emails}
            />
            {!types && errors.emails && (
              <Flex alignItems="flex-start" gap={1}>
                <Text color="red.700">
                  <FontAwesomeIcon icon={faWarning} />
                </Text>
                <Text
                  color="red.700"
                  sx={{
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal'
                  }}
                >
                  {errors.emails.message}
                </Text>
              </Flex>
            )}
            {types &&
              Object.entries(types).map(([email, msg]) => (
                <Flex alignItems="flex-start" gap={1}>
                  <Text color="red.700">
                    <FontAwesomeIcon icon={faWarning} />
                  </Text>
                  <Text
                    color="red.700"
                    sx={{
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal'
                    }}
                  >
                    <strong>{email}</strong> — {msg}
                  </Text>
                </Flex>
              ))}
          </Flex>

          <Flex justifyContent="flex-end" gap={2}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isValidating}
              isLoading={isValidating}
            >
              {buttonLabel} Reviewers
            </Button>
          </Flex>
        </Flex>
      </form>

      <ReviewersInfoWarningModal
        isOpen={warningNInfoModal}
        infoMessages={infoMessages}
        warningMessages={warningMessages}
        onCancel={() => {
          setWarningNInfoModal(false);
          setInfoMessages({});
          setWarningMessages({});
        }}
        onContinue={() => {
          const lines = getValues('emails').split('\n');
          const trimmedLines = lines.map(l => l.trim().toLowerCase());
          setWarningNInfoModal(false);
          setInfoMessages({});
          setWarningMessages({});
          onAddEmails(trimmedLines);
          onClose();
        }}
      />
    </Modal>
  );
};

export default AddEmailsModal;
